import React from "react";

interface TourCardProps {
  title: string;
  description: React.ReactNode;
  currentStep: number;
  totalSteps: number;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
  /** Position of the arrow: "left" | "right" | "top" | "bottom" */
  arrowPosition?: "left" | "right" | "top" | "bottom";
  /** Hide back/next buttons (step advances via interaction) */
  hideButtons?: boolean;
  /** Hide only the Next button — user must interact to advance */
  hideNext?: boolean;
  /** Disable the Next button (greyed out) */
  disableNext?: boolean;
  /** Hide the Skip/Back button on the left */
  hideSkip?: boolean;
}

const TourCard: React.FC<TourCardProps> = ({
  title,
  description,
  currentStep,
  totalSteps,
  onBack,
  onNext,
  onSkip,
  arrowPosition = "left",
  hideButtons = false,
  hideNext = false,
  disableNext = false,
  hideSkip = false,
}) => {
  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      {/* Arrow / Diamond — above card with directional shadow */}
      <div
        style={{
          position: "absolute",
          width: 9,
          height: 9,
          transform: "rotate(-45deg)",
          background: "#FFF",
          boxShadow: getArrowShadow(arrowPosition),
          zIndex: 2,
          ...getArrowPosition(arrowPosition),
        }}
      />

      {/* Card */}
      <div style={cardStyle}>
        {/* Title + Close */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", alignSelf: "stretch" }}>
          <div style={titleStyle}>{title}</div>
          <div onClick={onSkip} style={{ cursor: "pointer", flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4L12 12M12 4L4 12"
                stroke="#0C172C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Description */}
        <div style={descriptionStyle}>{description}</div>

        {/* Buttons row */}
        {!hideButtons && (
          <div style={buttonsRowStyle}>
            {!hideSkip &&
              (isFirst ? (
                <button onClick={onSkip} style={backButtonStyle}>
                  Skip
                </button>
              ) : (
                <button onClick={onBack} style={backButtonStyle}>
                  <BackArrowIcon />
                  <span>Back</span>
                </button>
              ))}
            {hideSkip && <div />}

            {!hideNext && (
              <button
                onClick={disableNext ? undefined : isLast ? onSkip : onNext}
                style={{
                  ...nextButtonStyle,
                  ...(disableNext
                    ? { color: "#AEAFC7", cursor: "default", opacity: 0.5, pointerEvents: "none" as const }
                    : {}),
                }}
              >
                <span>{isLast ? "Begin" : "Next"}</span>
                {!isLast && <NextArrowIcon color={disableNext ? "#AEAFC7" : "#EE4360"} />}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Arrow positioning ──────────────────────────────────────────

/** Directional shadow for the arrow diamond based on its position relative to the card */
function getArrowShadow(position: string): string {
  const s = "rgba(39, 55, 84, 0.08)";
  switch (position) {
    case "left":
      return `-2px -2px 4px ${s}`; // was top
    case "right":
      return `2px 2px 4px ${s}`;
    case "top":
      return `2px -2px 4px ${s}`;
    case "bottom":
      return `-2px 2px 4px ${s}`; // was left
    default:
      return `-2px 2px 4px ${s}`;
  }
}

function getArrowPosition(position: string): React.CSSProperties {
  switch (position) {
    case "left":
      return { left: -5, top: "50%", marginTop: -4.5 };
    case "right":
      return { right: -5, top: "50%", marginTop: -4.5 };
    case "top":
      return { top: -5, left: "50%", marginLeft: -4.5 };
    case "bottom":
      return { bottom: -5, left: "50%", marginLeft: -4.5 };
    default:
      return { left: -5, top: "50%", marginTop: -4.5 };
  }
}

// ─── Icons ──────────────────────────────────────────────────────

const BackArrowIcon = () => (
  <svg width="16" height="12" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.43754 0.240629C9.77857 0.550628 9.77857 1.06657 9.43754 1.37585L3.04065 7.19068L21.1168 7.19646C21.6089 7.19718 22 7.55271 22 8C22 8.4473 21.6089 8.81438 21.1168 8.81438L3.04065 8.8086L9.43754 14.6126C9.77857 14.9334 9.77857 15.4501 9.43754 15.7594C9.08459 16.0802 8.51621 16.0802 8.17597 15.7594L0.264715 8.56797C-0.0882397 8.25797 -0.0882397 7.74203 0.264715 7.43275L8.17597 0.240629C8.517 -0.0802095 9.08459 -0.0802095 9.43754 0.240629Z"
      fill="#011533"
    />
  </svg>
);

const NextArrowIcon = ({ color = "#EE4360" }: { color?: string }) => (
  <svg
    width="16"
    height="12"
    viewBox="0 0 22 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: "rotate(180deg)" }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.43754 0.240629C9.77857 0.550628 9.77857 1.06657 9.43754 1.37585L3.04065 7.19068L21.1168 7.19646C21.6089 7.19718 22 7.55271 22 8C22 8.4473 21.6089 8.81438 21.1168 8.81438L3.04065 8.8086L9.43754 14.6126C9.77857 14.9334 9.77857 15.4501 9.43754 15.7594C9.08459 16.0802 8.51621 16.0802 8.17597 15.7594L0.264715 8.56797C-0.0882397 8.25797 -0.0882397 7.74203 0.264715 7.43275L8.17597 0.240629C8.517 -0.0802095 9.08459 -0.0802095 9.43754 0.240629Z"
      fill={color}
    />
  </svg>
);

// ─── Styles ─────────────────────────────────────────────────────

const FONT: React.CSSProperties = { fontFamily: '"Inter", sans-serif' };

const cardStyle: React.CSSProperties = {
  display: "inline-flex",
  minWidth: 256,
  maxWidth: 300,
  padding: "8px 16px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: 0,
  borderRadius: 4,
  background: "#FFF",
  boxShadow: "0 2px 6px 0 rgba(39, 55, 84, 0.08)",
  zIndex: 1,
};

const titleStyle: React.CSSProperties = {
  color: "#0C172C",
  fontFeatureSettings: "'liga' off, 'clig' off",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "20px",
  alignSelf: "stretch",
  ...FONT,
};

const descriptionStyle: React.CSSProperties = {
  color: "#0C172C",
  fontFeatureSettings: "'liga' off, 'clig' off",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "20px",
  alignSelf: "stretch",
  marginTop: 4,
  ...FONT,
};

const buttonsRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  alignSelf: "stretch",
  marginTop: 16,
};

const backButtonStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 0,
  color: "#0C172C",
  fontFeatureSettings: "'liga' off, 'clig' off",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "20px",
  ...FONT,
};

const nextButtonStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 0,
  color: "#EE4360",
  fontFeatureSettings: "'liga' off, 'clig' off",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "20px",
  ...FONT,
};

export default TourCard;
